import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axiosConfig';
import { API_BASE_URL } from '../config';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  LinearProgress,
  Radio,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckIcon from '@mui/icons-material/Check';
import QuestionSpectrum from '../components/QuestionSpectrum';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from '@mui/material';

const AGE_RANGES = [
    { value: 'under14', label: 'Under 14' },
    { value: '14-18', label: '14-18' },
    { value: '18-22', label: '18-22' },
    { value: '23-45', label: '23-45' },
    { value: '46-65', label: '46-65' },
    { value: '65plus', label: '65+' }
];

const QuizPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  
  // State declarations
  const [introAnswers, setIntroAnswers] = useState({
    gender: '',
    age: '',
    education: '',
    subjects: ''
  });
  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [quiz, setQuiz] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasCredits, setHasCredits] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [credits, setCredits] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [showIntro, setShowIntro] = useState(true);
  const questionsPerPage = 12;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questionsLoaded, setQuestionsLoaded] = useState(0);
  const QUESTIONS_PER_PAGE = 12;
  const [submissionEnabled, setSubmissionEnabled] = useState(true);
  const [submissionError, setSubmissionError] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [step, setStep] = useState('intro'); // 'intro' or 'questions'
  const [introErrors, setIntroErrors] = useState({});
  const [showCreditPurchaseDialog, setShowCreditPurchaseDialog] = useState(false);

  // Debug log for render
  console.log('[QuizPage] Rendering with state:', {
    quizStarted,
    currentQuestion,
    questionsLoaded: questions.length,
    loading,
    credits
  });

  // Add this helper function at the top of your component
  const parseQuestion = (questionData) => {
    try {
      const options = Array.isArray(questionData.options) 
        ? questionData.options 
        : JSON.parse(questionData.options);
      
      return {
        ...questionData,
        question: questionData.question || questionData.text,
        leftLabel: options[0],
        rightLabel: options[1]
      };
    } catch (error) {
      console.error('Error parsing question options:', error);
      return questionData;
    }
  };

  // Update the fetchCredits function
  const fetchCredits = async () => {
    try {
        console.log('[QuizPage] Fetching credits for user...');
        const response = await axios.get('credits/available');
        
        console.log('[QuizPage] Credit check response:', response.data);
        
        if (response.data.success) {
            const availableCredits = response.data.availableCredits;
            const creditSource = response.data.creditSource;
            setHasCredits(availableCredits > 0);
            setCredits(availableCredits);
            
            if (availableCredits <= 0) {
                setMessage(creditSource === 'team' 
                    ? `Your team's credit pool is empty. Please contact your team leader to purchase more credits.`
                    : 'You have insufficient credits. Please purchase more to take the quiz.');
                return false;
            }
            return true;
        }
        return false;
    } catch (error) {
        console.error('[QuizPage] Error checking credits:', error);
        setMessage('Error checking credit availability. Please try again later.');
        setHasCredits(false);
        return false;
    }
};

  // Update the handleStartQuiz function
  const handleStartQuiz = async () => {
    if (loading) return;
    
    try {
        setLoading(true);
        console.log('[QuizPage] Starting quiz, checking credits...');
        
        // First check if we have credits
        const hasEnoughCredits = await fetchCredits();
        if (!hasEnoughCredits) {
            setShowCreditPurchaseDialog(true);
            return;
        }

        console.log('[QuizPage] Deducting credit...');
        
        // Match exactly the database structure
        const deductResponse = await axios.post('credits/deduct', {
            user_id: user.id,           // From the transactions table
            amount: -1,                 // Negative for deduction
            credits_added: -1,          // Number of credits to deduct
            status: 'completed',        // From the transactions table
            stripe_payment_id: null,    // Can be null for quiz deductions
            type: 'quiz_attempt'        // Identifying the transaction type
        });

        console.log('[QuizPage] Deduct response:', deductResponse.data);

        if (deductResponse.data.success) {
            setCredits(prev => prev - 1);
            setQuizStarted(true);
            setCurrentQuestion(0);
            setMessage('');
            console.log('[QuizPage] Quiz started successfully');
        } else {
            throw new Error(deductResponse.data.message || 'Failed to deduct credits');
        }

    } catch (error) {
        console.error('[QuizPage] Error details:', error.response?.data || error.message);
        setMessage(error.response?.data?.message || 'Failed to start quiz. Please try again.');
    } finally {
        setLoading(false);
    }
};

  // Update the useEffect to use the moved fetchCredits function
  useEffect(() => {
    if (token) {
      fetchCredits();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch quiz data on mount
  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        setLoading(true);
        console.log('Fetching quiz data...');
        
        const response = await axios.get('quiz/questions');
        console.log('Raw quiz data response:', response.data);
        
        if (response.data.success && Array.isArray(response.data.questions)) {
          const formattedQuestions = response.data.questions.map(q => ({
            id: q.id,
            question: q.text,
            options: Array.isArray(q.options) 
              ? q.options 
              : (typeof q.options === 'string' ? JSON.parse(q.options) : []),
            category: q.category,
            metadata: q.metadata || {}
          }));
          
          console.log('Formatted questions:', formattedQuestions);
          setQuestions(formattedQuestions);
          setQuestionsLoaded(formattedQuestions.length);
        } else {
          console.error('Invalid quiz data structure:', response.data);
          setMessage('Error: Invalid quiz data received');
        }
      } catch (error) {
        console.error('Quiz data fetch error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setMessage('Failed to load quiz questions. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizData();
  }, [token]);

  const handleAnswer = (questionId, answer) => {
    console.log('[QuizPage] Answer selected:', { questionId, answer });
    
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      console.log('[QuizPage] Quiz complete, submitting...');
      handleSubmitQuiz();
    }
  };

  const handleSubmitQuiz = async () => {
    try {
        setIsSubmitting(true);
        console.log('[QuizPage] Preparing quiz submission...');

        const formattedAnswers = questions.map(question => ({
            questionId: question.id,
            value: answers[question.id] || 0,
            category: question.category || 'learning_preference'
        }));

        console.log('[QuizPage] Submitting answers:', formattedAnswers);

        const response = await axios.post('quiz/submit', {
            answers: formattedAnswers,
            demographics: introAnswers
        });

        console.log('[QuizPage] Submission response:', response.data);

        if (response.data.success) {
            // Extract the result ID from the response
            const resultId = response.data.resultId || 
                           response.data.id || 
                           response.data.data?.id;
            
            if (!resultId) {
                throw new Error('No result ID in response');
            }

            // Store both the ID and the full results in localStorage
            localStorage.setItem('lastQuizResultId', resultId);
            localStorage.setItem('lastQuizResults', JSON.stringify(response.data));
            
            console.log('[QuizPage] Navigating to results with ID:', resultId);
            
            // Navigate to the results page with the full state
            navigate(`/quiz/results/${resultId}`);
            
        } else {
            throw new Error(response.data.message || 'Failed to submit quiz');
        }

    } catch (error) {
        console.error('[QuizPage] Submission error:', error);
        setMessage('Failed to submit quiz. Please try again.');
    } finally {
        setIsSubmitting(false);
    }
};

  const handleInsufficientCredits = () => {
    navigate('/credits', {
        state: { 
            returnTo: '/quiz',
            currentCredits: credits 
        }
    });
  };

  const handleAnswerChange = (questionId, value) => {
    if (!questionId || !questions.find(q => q.id === questionId)) {
      console.log('Invalid question ID:', questionId);
      return;
    }
    
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [questionId]: value
      };
      
      console.log('Updated answers:', {
        questionId,
        value,
        totalAnswers: Object.keys(newAnswers).length,
        totalQuestions: questions.length
      });
      
      return newAnswers;
    });
  };

  // Add intro questions render
  const renderIntroQuestions = () => {
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Before we begin, please tell us about yourself:
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={!!introErrors.gender}>
                        <InputLabel>Gender</InputLabel>
                        <Select
                            value={introAnswers.gender}
                            onChange={(e) => handleIntroChange('gender', e.target.value)}
                        >
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                            <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                        </Select>
                        {introErrors.gender && (
                            <FormHelperText>{introErrors.gender}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!introErrors.age} required>
                        <InputLabel>Age Range</InputLabel>
                        <Select
                            value={introAnswers.age}
                            label="Age Range"
                            onChange={(e) => handleIntroChange('age', e.target.value)}
                        >
                            {AGE_RANGES.map((range) => (
                                <MenuItem key={range.value} value={range.value}>
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {introErrors.age && (
                            <FormHelperText>{introErrors.age}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth required error={!!introErrors.education}>
                        <InputLabel>Education Level</InputLabel>
                        <Select
                            value={introAnswers.education}
                            onChange={(e) => handleIntroChange('education', e.target.value)}
                        >
                            <MenuItem value="high_school">High School</MenuItem>
                            <MenuItem value="bachelors">Bachelor's Degree</MenuItem>
                            <MenuItem value="masters">Master's Degree</MenuItem>
                            <MenuItem value="doctorate">Doctorate</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                        {introErrors.education && (
                            <FormHelperText>{introErrors.education}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="What subjects are you currently learning? (Optional)"
                        multiline
                        rows={2}
                        value={introAnswers.subjects}
                        onChange={(e) => handleIntroChange('subjects', e.target.value)}
                    />
                </Grid>
            </Grid>
            <Button
                variant="contained"
                color="primary"
                onClick={handleStartQuiz}
                sx={{ mt: 3 }}
            >
                Start Quiz
            </Button>
        </Box>
    );
  };

  // Add this helper for spectrum labels
  const getSpectrumLabels = (question) => {
    const defaultLabels = {
      left: question.leftLabel || 'Strongly Disagree',
      right: question.rightLabel || 'Strongly Agree'
    };
    return defaultLabels;
  };

  // Update the progress bar render function
  const renderProgressBar = () => {
    const totalQuestions = questions.length;
    const answeredQuestions = Object.keys(answers).length;
    const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

    return (
      <Box sx={{ width: '100%', mb: 4 }}>
        <LinearProgress 
          variant="determinate" 
          value={progressPercentage}
          sx={{ 
            height: 10,
            borderRadius: 5,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 5,
              backgroundColor: 'primary.main',
            }
          }}
        />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 1,
          px: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            Progress: {answeredQuestions} of {totalQuestions} questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progressPercentage)}%
          </Typography>
        </Box>
      </Box>
    );
  };

  // Modified spectrum render function
  const renderSpectrum = (question) => {
    const parsedQuestion = parseQuestion(question);
    
    return (
        <Box sx={{ mb: 4 }}>
            <Typography 
                variant="h6" 
                align="center" 
                gutterBottom 
                sx={{ 
                    mb: 3,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    px: { xs: 1, sm: 2 }
                }}
            >
                {parsedQuestion.question || parsedQuestion.text}
            </Typography>

            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',  // Keep row direction for all screen sizes
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: { xs: 1, sm: 3 },
                width: '100%'
            }}>
                {/* Left option */}
                <Typography 
                    variant="body1" 
                    sx={{ 
                        width: { xs: '80px', sm: '200px' }, 
                        textAlign: 'right',
                        flexShrink: 0,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                >
                    {Array.isArray(parsedQuestion.options) ? parsedQuestion.options[0] : 'Left Option'}
                </Typography>

                {/* Radio buttons group */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    flex: 1,
                    maxWidth: { xs: '160px', sm: '300px' },
                    mx: { xs: 1, sm: 2 }
                }}>
                    {[1, 2, 3, 4, 5].map((value) => (
                        <Radio
                            key={value}
                            checked={answers[parsedQuestion.id] === value}
                            onChange={() => handleAnswerChange(parsedQuestion.id, value)}
                            value={value}
                            name={`question-${parsedQuestion.id}`}
                            sx={{
                                padding: { xs: 0.5, sm: 1.5 },
                                '& .MuiSvgIcon-root': {
                                    fontSize: { xs: '1.2rem', sm: '1.5rem' }
                                }
                            }}
                        />
                    ))}
                </Box>

                {/* Right option */}
                <Typography 
                    variant="body1" 
                    sx={{ 
                        width: { xs: '80px', sm: '200px' }, 
                        textAlign: 'left',
                        flexShrink: 0,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                >
                    {Array.isArray(parsedQuestion.options) ? parsedQuestion.options[1] : 'Right Option'}
                </Typography>
            </Box>
        </Box>
    );
  };

  // Modified legend render function
  const renderLegend = () => (
    <Box sx={{ 
        mb: { xs: 3, sm: 6 }, 
        textAlign: 'center',
        px: { xs: 2, sm: 0 }
    }}>
        <Typography 
            variant="body1" 
            gutterBottom
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
            Select based on how closely you relate to each of the options.
        </Typography>
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '400px',
            margin: '0 auto',
            mt: 2,
            px: { xs: 2, sm: 4 }
        }}>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Agree
            </Typography>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Slightly Agree
            </Typography>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Neutral
            </Typography>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Slightly Agree
            </Typography>
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Agree
            </Typography>
        </Box>
    </Box>
  );

  // Add this helper function to get current page questions
  const getCurrentPageQuestions = useCallback(() => {
    if (!questions || !Array.isArray(questions) || questions.length === 0) {
      console.log('No questions available');
      return [];
    }
    
    const startIndex = currentPage * QUESTIONS_PER_PAGE;
    const pageQuestions = questions.slice(startIndex, startIndex + QUESTIONS_PER_PAGE);
    
    console.log('Current page questions:', {
      page: currentPage,
      start: startIndex,
      count: pageQuestions.length
    });
    
    return pageQuestions;
  }, [currentPage, questions]);

  // Add helper function to check if current page is complete
  const isCurrentPageComplete = () => {
    const currentQuestions = getCurrentPageQuestions();
    return currentQuestions.every(q => answers.hasOwnProperty(q.id));
  };

  // Update handleNextPage
  const handleNextPage = () => {
    console.log('handleNextPage called'); // Debug log
    
    if (!isCurrentPageComplete()) {
      setMessage('Please answer all questions on this page before continuing');
      return;
    }
    
    const maxPages = Math.ceil(questions.length / questionsPerPage);
    if (currentPage < maxPages - 1) {
      setCurrentPage(prev => prev + 1);
      setMessage(''); // Clear any messages
      
      // Add a small delay to ensure state update has completed
      setTimeout(() => {
        console.log('Scrolling to top'); // Debug log
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  // Update handlePrevPage
  const handlePrevPage = () => {
    console.log('handlePrevPage called'); // Debug log
    
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
      
      // Add a small delay to ensure state update has completed
      setTimeout(() => {
        console.log('Scrolling to top'); // Debug log
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  // Add progress indicator
  const renderProgress = () => {
    const progress = (Object.keys(answers).length / questions.length) * 100;
    return (
      <Box sx={{ width: '100%', mb: 2 }}>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ height: 10, borderRadius: 5 }}
        />
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          {Object.keys(answers).length} of {questions.length} questions answered
        </Typography>
      </Box>
    );
  };

  // Add this near the top of your component
  useEffect(() => {
    console.log('Current answers:', answers);
    console.log('Total questions:', questions.length);
    console.log('Answers length:', Object.keys(answers).length);
    console.log('Submit should be enabled:', Object.keys(answers).length === questions.length);
  }, [answers, questions]);

  // Add this useEffect to monitor answers state
  useEffect(() => {
    console.log('Current answers state:', answers);
    console.log('Number of answers:', Object.keys(answers).length);
  }, [answers]);

  // Simplify the startNewQuiz function
  const startNewQuiz = () => {
    setAnswers({});
    setCurrentPage(0);
    localStorage.removeItem('quizProgress');
    localStorage.removeItem('lastQuizResult');
    setMessage('');
  };

  // Update the initialization useEffect to remove progress restoration
  useEffect(() => {
    // Just start fresh every time
    startNewQuiz();
  }, []);

  // Add this useEffect to clean up answers when questions load
  useEffect(() => {
    if (questions?.length > 0) {
      // Clean up answers to only include valid question IDs
      const validQuestionIds = questions.map(q => q.id);
      const cleanedAnswers = Object.entries(answers)
        .filter(([key]) => validQuestionIds.includes(Number(key)))
        .reduce((acc, [key, value]) => ({
          ...acc,
          [key]: value
        }), {});
      
      console.log('Cleaning answers:', {
        before: Object.keys(answers).length,
        after: Object.keys(cleanedAnswers).length,
        validIds: validQuestionIds
      });
      
      setAnswers(cleanedAnswers);
    }
  }, [questions]); // Only run when questions change

  // Update shouldEnableSubmit
  const shouldEnableSubmit = useCallback(() => {
    if (!questions || questions.length === 0) return false;
    
    const validAnswers = Object.keys(answers)
      .filter(key => questions.find(q => q.id === Number(key)))
      .length;
      
    console.log('Submit validation:', {
      totalQuestions: questions.length,
      validAnswers,
      answers: Object.keys(answers).sort((a,b) => Number(a) - Number(b)),
      questionIds: questions.map(q => q.id).sort((a,b) => a - b)
    });
    
    return validAnswers === questions.length;
  }, [answers, questions]);

  // Add this function near your other render functions
  const renderLoadingOverlay = () => {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Processing Quiz...
        </Typography>
      </Box>
    );
  };

  // Also make sure you have renderNavigationButtons defined
  const renderNavigationButtons = () => {
    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: { xs: 2, sm: 0 },
            mt: 4,
            mb: 2 
        }}>
            <Button
                variant="contained"
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                startIcon={<NavigateBeforeIcon />}
                fullWidth={isMobile}
            >
                Previous
            </Button>

            {/* Page indicator */}
            <Typography sx={{ 
                alignSelf: 'center',
                order: { xs: -1, sm: 0 }
            }}>
                Page {currentPage + 1} of {Math.ceil(questions.length / questionsPerPage)}
            </Typography>

            {/* Show either Next or Submit button */}
            <Button
                variant="contained"
                onClick={currentPage === Math.ceil(questions.length / questionsPerPage) - 1 
                    ? handleSubmitQuiz 
                    : handleNextPage}
                disabled={currentPage === Math.ceil(questions.length / questionsPerPage) - 1 
                    ? Object.keys(answers).length !== questions.length || !submissionEnabled
                    : !isCurrentPageComplete()}
                endIcon={currentPage === Math.ceil(questions.length / questionsPerPage) - 1 
                    ? <CheckIcon /> 
                    : <NavigateNextIcon />}
                fullWidth={isMobile}
                sx={{
                    backgroundColor: isCurrentPageComplete() ? 'primary.main' : 'grey.400',
                    '&:hover': {
                        backgroundColor: isCurrentPageComplete() ? 'primary.dark' : 'grey.500'
                    }
                }}
            >
                {currentPage === Math.ceil(questions.length / questionsPerPage) - 1 
                    ? `Submit Quiz (${Object.keys(answers).length}/${questions.length})`
                    : 'Next'}
            </Button>
        </Box>
    );
  };

  // Add this function to check submission status
  const checkSubmissionStatus = async () => {
    try {
        const response = await axios.get('quiz/submission-status');
        const data = response.data;
        setSubmissionEnabled(data.enabled);
        setSubmissionError(data.message);
    } catch (error) {
        console.error('Failed to check submission status:', error);
        // Default to enabled if check fails
        setSubmissionEnabled(true);
        setSubmissionError(null);
    }
  };

  // Add a useEffect to process questions when they're loaded
  useEffect(() => {
    if (questions?.length > 0) {
      // Process questions to ensure they have the correct format
      const processedQuestions = questions.map(q => parseQuestion(q));
      setQuestions(processedQuestions);
      
      console.log('Processed questions:', processedQuestions);
    }
  }, [questions.length]); // Only run when questions array length changes

  // Add this handler
  const handleIntroChange = (field, value) => {
    setIntroAnswers(prev => ({
        ...prev,
        [field]: value
    }));
    // Clear error when field is updated
    if (introErrors[field]) {
        setIntroErrors(prev => ({
            ...prev,
            [field]: ''
        }));
    }
  };

  // Add credit purchase dialog
  const renderCreditPurchaseDialog = () => (
    <Dialog open={showCreditPurchaseDialog} onClose={() => setShowCreditPurchaseDialog(false)}>
        <DialogTitle>Insufficient Credits</DialogTitle>
        <DialogContent>
            <Box sx={{ p: 2 }}>
                <Typography gutterBottom>
                    You need 1 credit to take the quiz.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Current credits: {credits}
                </Typography>
                <Stack spacing={2}>
                    {user?.team_leader_id ? (
                        // Team member view
                        <Button
                            variant="contained"
                            onClick={() => {
                                navigate('/contact-team-leader', {
                                    state: { 
                                        reason: 'credits',
                                        currentCredits: credits 
                                    }
                                });
                            }}
                        >
                            Contact Team Leader
                        </Button>
                    ) : (
                        // Individual user view
                        <>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    navigate('/credits', {  // Changed from '/credits/purchase'
                                        state: { 
                                            returnTo: '/quiz',
                                            currentCredits: credits 
                                        }
                                    });
                                }}
                            >
                                View Credits
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    navigate('/credits/history');
                                }}
                            >
                                View Credit History
                            </Button>
                        </>
                    )}
                </Stack>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setShowCreditPurchaseDialog(false)}>
                Close
            </Button>
        </DialogActions>
    </Dialog>
  );

  // Main render
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : !hasCredits ? (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Learning Style Quiz
          </Typography>
          <Typography gutterBottom>
            Available Credits: {credits !== null ? credits : 'Loading...'}
          </Typography>
          {user?.team_leader_id && (
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Using team credit pool
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/credits')}  // Changed from handleInsufficientCredits
            sx={{ mt: 2 }}
          >
            View Credits
          </Button>
        </Box>
      ) : !quizStarted ? (
        // Show intro questions
        <Box>
          {showIntro && renderIntroQuestions()}
        </Box>
      ) : (
        // Show main quiz
        <Box>
          {renderLegend()}
          {renderProgressBar()}
          
          {/* Render current page questions */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mb: 4 }}>
                {getCurrentPageQuestions().map((question) => (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    {renderSpectrum(question)}
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </AnimatePresence>

          {/* Navigation buttons */}
          {renderNavigationButtons()}
        </Box>
      )}

      {/* Messages and loading overlay */}
      {message && (
        <Alert 
          severity={message.includes('success') ? 'success' : 'error'}
          sx={{ mt: 2 }}
        >
          {message}
        </Alert>
      )}
      
      {isSubmitting && renderLoadingOverlay()}

      {/* Add the credit purchase dialog */}
      {renderCreditPurchaseDialog()}
    </Container>
  );
};

export default QuizPage;
